import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { FormTemplate } from '../../models/question/form-template';
import { QuestionBase } from '../../models/question/question-base';
import { QuestionControlService } from '../dynamic-form-question/question-control.service';

@Component({
    selector: 'app-dynamic-form',
    templateUrl: './dynamic-form.component.html',
    styleUrls: ['./dynamic-form.component.scss'],
    standalone: false
})
export class DynamicFormComponent implements OnInit, OnChanges {
    @Input() dynamicForm: FormTemplate;
    @Input() submitText: string;
    @Input() disableSubmit: boolean;

    @Output() submitForm = new EventEmitter();
    @Output() subFormTriggered = new EventEmitter<any>();

    form: UntypedFormGroup;
    showFormError: boolean = false;
    questions: QuestionBase<any>[] = [];

    constructor(private questionControlService: QuestionControlService) { }

    ngOnInit() {
        this.buildForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.dynamicForm && !changes.dynamicForm.firstChange) {
            this.buildForm();
        }
    }

    private buildForm(): void {
        if (!this.dynamicForm?.formPanels) return;

        const existingValues = this.form?.value || {};

        this.questions = [];

        this.dynamicForm.formPanels.forEach((formPanel) => {
            if (formPanel.items && formPanel.items.length > 0) {
                const formItems = formPanel.items.filter(
                    (item) => item.controlType != 'title' && item.controlType != 'description',
                );

                this.questions.push(...formItems);
            }
        });

        this.form = this.questionControlService.toFormGroup(this.questions);

        Object.keys(existingValues).forEach((key) => {
            if (this.form.controls[key]) {
                this.form.controls[key].setValue(existingValues[key]);
            }
        });
    }

    onSubFormTriggered(event: any): void {
        const currentValues = { ...(this.form?.value || {}) };
        const questionKey = event?.question?.key;
        const isMultiSelect = Array.isArray(event?.selectedValue);
        const isRemoval = event?.isSubFormRemoval;
        const subFormId = event?.subFormId?.toString();

        if (questionKey && this.dynamicForm?.formPanels) {
            this.dynamicForm.formPanels.forEach((panel) => {
                panel.items?.forEach((item: any) => {
                    if (!item._isSubFormItem || !item.key || item._subFormQuestionKey !== questionKey) {
                        return;
                    }

                    if (isRemoval) {
                        if (item._subFormId !== subFormId) return;
                    } else if (isMultiSelect) {
                        return;
                    }

                    delete currentValues[item.key];
                    this.form?.controls[item.key]?.setValue('', { emitEvent: false });
                });
            });
        }

        this.subFormTriggered.emit({
            ...event,
            currentValues,
        });
    }

    onSubmit() {
        this.questions.forEach((question) => {
            if (question.controlType == 'checkbox' && question.required) {
                if (this.form.controls[question.key].value == false) {
                    this.form.controls[question.key].setErrors({ incorrect: true });
                }
            }

            if (question.type == 'number' && !question.required) {
                if (this.form.controls[question.key].value == '') {
                    this.form.controls[question.key].setValue(null);
                }
            }
        });

        if (this.form.invalid) {
            this.showFormError = true;
            return;
        }

        const submitted = { ...this.form.value };

        // Disabled controls are omitted from form.value. Read-only "today" dates are filled by
        // the form itself and must still be submitted; other read-only fields stay excluded.
        this.questions.forEach((question) => {
            const control = this.form.controls[question.key];

            if (
                question.controlType === 'datepicker' &&
                question.isToday &&
                question.readonly &&
                control?.disabled
            ) {
                submitted[question.key] = control.value;
            }
        });

        this.submitForm.emit(submitted);
    }
}
